import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Text,
  Title,
  Rows,
  Columns,
  Column,
  Select,
  LoadingIndicator,
} from "@canva/app-ui-kit";
import { addElementAtPoint, createRichtextRange } from "@canva/design";
import { upload } from "@canva/asset";
import {
  api,
  type FinancialModel,
  type MetricValue,
  type MetricsResponse,
} from "../api/client";
import { SlidesSection } from "./slides";

const CHART_TYPES = [
  { value: "revenue", label: "Revenue" },
  { value: "revenue_vs_expenses", label: "Revenue vs Expenses" },
  { value: "pnl_summary", label: "P&L Summary" },
  { value: "cash_balance", label: "Cash Balance" },
  { value: "burn", label: "Burn Rate" },
  { value: "expense_breakdown", label: "Expense Breakdown" },
  { value: "headcount", label: "Headcount" },
] as const;

/** Ordered list of metrics to display in the panel. */
const METRIC_KEYS = [
  "arr",
  "mrr",
  "totalRevenue",
  "grossMargin",
  "burnRate",
  "runway",
  "cashBalance",
  "headcount",
  "netIncome",
] as const;

interface DashboardProps {
  orgId: string;
  orgName: string;
  orgs: Array<{ orgId: string; orgName: string }>;
  onSelectOrg: (orgId: string) => void;
  onLinkAnother: () => void;
  onDisconnect: () => void;
}

export function Dashboard({
  orgId,
  orgName,
  orgs,
  onSelectOrg,
  onLinkAnother,
  onDisconnect,
}: DashboardProps) {
  const [models, setModels] = useState<FinancialModel[]>([]);
  const [selectedModelId, setSelectedModelId] = useState<string>("");
  const [selectedScenarioId, setSelectedScenarioId] = useState<string>("");
  const [month, setMonth] = useState<string>(getCurrentMonth());
  const [metrics, setMetrics] = useState<MetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [insertingMetric, setInsertingMetric] = useState<string | null>(null);
  const [insertingChart, setInsertingChart] = useState<string | null>(null);
  const [pickedMetric, setPickedMetric] = useState<string>(METRIC_KEYS[0]);
  const [pickedChart, setPickedChart] = useState<string>(CHART_TYPES[0].value);

  // Load models on mount
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { models } = await api.getScenarios();
        if (cancelled) return;
        setModels(models);
        if (models.length > 0) {
          setSelectedModelId(models[0].id);
          if (models[0].scenarios.length > 0) {
            setSelectedScenarioId(models[0].scenarios[0].id);
          }
        }
      } catch {
        // Error handled by empty state
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  // Fetch metrics when scenario or month changes
  useEffect(() => {
    if (!selectedScenarioId || !month) return;
    let cancelled = false;

    async function load() {
      try {
        const data = await api.getMetrics(selectedScenarioId, month);
        if (!cancelled) setMetrics(data);
      } catch {
        if (!cancelled) setMetrics(null);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [selectedScenarioId, month]);

  const selectedModel = models.find((m) => m.id === selectedModelId);

  const insertText = useCallback(async (key: string, metric: MetricValue) => {
    setInsertingMetric(key);
    try {
      const range = createRichtextRange();

      // Label line — small, secondary color
      const labelBounds = range.appendText(metric.label + "\n", {
        color: "#6b7280",
        fontWeight: "normal",
      });
      range.formatParagraph(labelBounds.bounds, { fontSize: 14 });

      // Value line — large, bold
      const valueLine = metric.change != null && metric.change !== 0
        ? metric.formatted + "\n"
        : metric.formatted;
      const valueBounds = range.appendText(valueLine, {
        color: "#000000",
        fontWeight: "bold",
      });
      range.formatParagraph(valueBounds.bounds, { fontSize: 36 });

      // Change line — small, green/red
      if (metric.change != null && metric.change !== 0) {
        const arrow = metric.change > 0 ? "▲" : "▼";
        const changeText = `${arrow} ${Math.abs(metric.change)}% MoM`;
        const changeBounds = range.appendText(changeText, {
          color: metric.change > 0 ? "#22c55e" : "#ef4444",
          fontWeight: "normal",
        });
        range.formatParagraph(changeBounds.bounds, { fontSize: 12 });
      }

      await addElementAtPoint({ type: "richtext", range });
    } finally {
      setInsertingMetric(null);
    }
  }, []);

  const insertChart = useCallback(
    async (chartType: string) => {
      setInsertingChart(chartType);
      try {
        const { dataUrl, width, height } = await api.getChart(
          selectedScenarioId,
          chartType,
          12,
          month
        );

        const asset = await upload({
          type: "image",
          mimeType: "image/png",
          url: dataUrl,
          thumbnailUrl: dataUrl,
          aiDisclosure: "none",
        });

        await addElementAtPoint({
          type: "image",
          ref: asset.ref,
          altText: { text: "Chart", decorative: true },
          width,
          height,
        });
      } finally {
        setInsertingChart(null);
      }
    },
    [selectedScenarioId, month]
  );

  if (loading) {
    return <LoadingIndicator />;
  }

  return (
    <Rows spacing="1.5u">
      {/* Header */}
      <Columns spacing="1u" alignY="center">
        <Column width="fluid">
          <Title size="small">Profitual</Title>
        </Column>
        <Column width="content">
          {orgs.length > 1 ? (
            <Select
              options={orgs.map((o) => ({ value: o.orgId, label: o.orgName || o.orgId }))}
              value={orgId}
              onChange={onSelectOrg}
            />
          ) : (
            <Text size="small" tone="tertiary">{orgName}</Text>
          )}
        </Column>
      </Columns>

      {/* Selectors */}
      {models.length > 0 ? (
        <>
          <Select
            options={models.map((m) => ({ value: m.id, label: m.name }))}
            value={selectedModelId}
            onChange={setSelectedModelId}
            stretch
          />
          {selectedModel && (
            <Select
              options={selectedModel.scenarios.map((s) => ({
                value: s.id,
                label: s.name,
              }))}
              value={selectedScenarioId}
              onChange={setSelectedScenarioId}
              stretch
            />
          )}
          <Select
            options={getMonthOptions()}
            value={month}
            onChange={setMonth}
            stretch
          />
        </>
      ) : (
        <Text tone="tertiary">
          No financial models found. Create one in Profitual first.
        </Text>
      )}

      {/* Slide Templates */}
      {selectedScenarioId && (
        <SlidesSection scenarioId={selectedScenarioId} month={month} />
      )}

      {/* Metrics read-out */}
      {metrics && (
        <>
          <Title size="xsmall">Key Metrics</Title>
          {METRIC_KEYS.map((key) => {
            const metric = metrics.metrics[key];
            if (!metric) return null;
            const change = metric.change;
            const hasChange = change != null && change !== 0;
            const arrow = hasChange ? (change! > 0 ? "▲" : "▼") : "";
            const color = hasChange ? (change! > 0 ? "#22c55e" : "#ef4444") : undefined;
            return (
              <Columns key={key} spacing="0.5u" alignY="center">
                <Column width="fluid">
                  <Text size="small">{metric.label}</Text>
                </Column>
                <Column width="content">
                  <Text size="small" alignment="end">
                    {metric.formatted}
                    {hasChange && (
                      <span style={{ marginLeft: 4, fontSize: 10, color }}>
                        {arrow} {Math.abs(change!)}%
                      </span>
                    )}
                  </Text>
                </Column>
              </Columns>
            );
          })}
        </>
      )}

      {/* KPI picker */}
      {metrics && (
        <>
          <Title size="xsmall">Add KPI</Title>
          <Select
            options={METRIC_KEYS.map((key) => ({
              value: key,
              label: metrics.metrics[key]?.label ?? key,
            }))}
            value={pickedMetric}
            onChange={setPickedMetric}
            stretch
          />
          <Button
            variant="secondary"
            onClick={() => {
              const m = metrics.metrics[pickedMetric];
              if (m) insertText(pickedMetric, m);
            }}
            loading={insertingMetric === pickedMetric}
            stretch
          >
            Insert KPI
          </Button>
        </>
      )}

      {/* Chart picker */}
      {selectedScenarioId && (
        <>
          <Title size="xsmall">Add chart</Title>
          <Select
            options={CHART_TYPES.map(({ value, label }) => ({ value, label }))}
            value={pickedChart}
            onChange={setPickedChart}
            stretch
          />
          <Button
            variant="secondary"
            onClick={() => insertChart(pickedChart)}
            loading={insertingChart === pickedChart}
            stretch
          >
            Insert chart
          </Button>
        </>
      )}

      {/* Org management */}
      <Button variant="tertiary" onClick={onLinkAnother} stretch>
        Link another organization
      </Button>
      <Button variant="tertiary" onClick={onDisconnect} stretch>
        {orgs.length > 1 ? `Disconnect from ${orgName}` : "Disconnect account"}
      </Button>
    </Rows>
  );
}

function getCurrentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function getMonthOptions() {
  const options: { value: string; label: string }[] = [];
  const now = new Date();
  // Show 24 months: 12 past + 12 future
  for (let i = -12; i <= 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    });
    options.push({ value, label });
  }
  return options;
}
